import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import facturaCompraService, { FacturaCompra } from '../../services/facturaCompraService';
import variablesSistemaService from '../../services/variablesSistemaService';
import { formatCurrency, formatDate } from '../../utils/formatters';

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const d = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(d.getTime())) return '-';
  return formatDate(d.toISOString().split('T')[0]);
};

/** Formato Letra-Suc-Nro: A-0001-00000001 */
const formatoLetraSucNro = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

const FacturaCompraDetalle: React.FC = () => {
  const navigate = useNavigate();
  const { nroFormulario } = useParams<{ nroFormulario: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [factura, setFactura] = useState<FacturaCompra | null>(null);
  const [loading, setLoading] = useState(true);
  const [openImpuestosDialog, setOpenImpuestosDialog] = useState(false);
  const [impuestoTasas, setImpuestoTasas] = useState<{
    impuestotasa1?: number;
    impuestotasa2?: number;
    impuestotasa3?: number;
    impuestotasa4?: number;
  }>({});

  const loadFactura = useCallback(async () => {
    const id = parseInt(nroFormulario || '', 10);
    if (isNaN(id)) {
      enqueueSnackbar('Factura no válida', { variant: 'error' });
      navigate('/compras/facturas');
      return;
    }
    try {
      setLoading(true);
      const data = await facturaCompraService.getFacturaCompra(id);
      setFactura(data);
    } catch (error) {
      console.error('Error al cargar factura:', error);
      enqueueSnackbar('Error al cargar la factura de compra', { variant: 'error' });
      navigate('/compras/facturas');
    } finally {
      setLoading(false);
    }
  }, [nroFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    loadFactura();
  }, [loadFactura]);

  useEffect(() => {
    variablesSistemaService.getImpuestoTasas()
      .then(setImpuestoTasas)
      .catch(() => {});
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#f59e0b' }} />
      </Box>
    );
  }

  if (!factura) {
    return null;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/compras/facturas')}
              sx={{ color: '#f59e0b' }}
            >
              Volver
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#f59e0b' }}>
              Factura de Compra {formatoLetraSucNro(factura.letra, factura.nroSuc, factura.nroComprobante ?? undefined)}
            </Typography>
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#f59e0b' }}>
            Encabezado
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Nro. Formulario</Typography>
              <Typography variant="body1">{factura.nroFormulario}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Fecha emisión</Typography>
              <Typography variant="body1">{formatearFecha(factura.fechaEmision)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Nro Comprobante</Typography>
              <Typography variant="body1">{formatoLetraSucNro(factura.letra, factura.nroSuc, factura.nroComprobante ?? undefined)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Fecha de vencimiento</Typography>
              <Typography variant="body1">{formatearFecha(factura.fechaVencim)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Fecha de aplicación de IVA</Typography>
              <Typography variant="body1">{formatearFecha(factura.aplicacionIva)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Proveedor</Typography>
              <Typography variant="body1">{factura.nroProveedor} - {factura.nombre || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">CUIT</Typography>
              <Typography variant="body1">{factura.cuit || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Forma de pago</Typography>
              <Typography variant="body1">{factura.formaDePago || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Ubicación destino</Typography>
              <Typography variant="body1">
                {factura.nroUbicacionDestino != null
                  ? factura.ubicacionDestinoDescripcion
                    ? `${factura.nroUbicacionDestino} - ${factura.ubicacionDestinoDescripcion}`
                    : String(factura.nroUbicacionDestino)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Usuario</Typography>
              <Typography variant="body1">{factura.usuario || '-'}</Typography>
            </Grid>
            {factura.descripcion && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Descripción</Typography>
                <Typography variant="body1">{factura.descripcion}</Typography>
              </Grid>
            )}
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#f59e0b' }}>
            Detalle
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Cantidad</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Precio lista</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">% Desc</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Precio unit.</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Descuento</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(factura.detalles || []).map((d, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {(d.tipoProveedor ?? '').trim().toUpperCase() === 'G' ? 'Concepto' : 'Producto'}
                    </TableCell>
                    <TableCell>{d.codigoArticulo || '-'}</TableCell>
                    <TableCell>{d.descripcionArticulo || '-'}</TableCell>
                    <TableCell align="right">{Number(d.cantidad)?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) ?? '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(d.precioLista ?? 0)}</TableCell>
                    <TableCell align="right">{Number(d.porcDesc)?.toFixed(1) ?? '-'}%</TableCell>
                    <TableCell align="right">{formatCurrency(d.precioUnitario ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(d.descuento ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(d.subtotal ?? 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 2, color: '#f59e0b' }}>
            Totales
          </Typography>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Subtotal</Typography>
                  <Typography>{formatCurrency(factura.subtotal ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    Descuento
                    {[factura.porcDesc, factura.porcDesc2, factura.porcDesc3]
                      .filter((p) => p != null && Number(p) > 0).length > 0 && (
                      <> ({[factura.porcDesc, factura.porcDesc2, factura.porcDesc3]
                        .filter((p) => p != null && Number(p) > 0)
                        .map((p) => `${Number(p).toFixed(1)}%`)
                        .join(', ')})
                      </>
                    )}
                  </Typography>
                  <Typography>{formatCurrency(factura.descuento ?? 0)}</Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Subtotal gravado</Typography>
                  <Typography>{formatCurrency(factura.importeSinIva ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    IVA {impuestoTasas.impuestotasa1 != null ? `${impuestoTasas.impuestotasa1}%` : '1'}
                  </Typography>
                  <Typography>{formatCurrency(factura.ivainscripto ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    IVA {impuestoTasas.impuestotasa2 != null ? `${impuestoTasas.impuestotasa2}%` : '2'}
                  </Typography>
                  <Typography>{formatCurrency(factura.ivanoInscripto ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    IVA {impuestoTasas.impuestotasa3 != null ? `${impuestoTasas.impuestotasa3}%` : '3'}
                  </Typography>
                  <Typography>{formatCurrency(factura.ivaotro ?? 0)}</Typography>
                </Stack>
                {(factura.letra ?? '').trim().toUpperCase() === 'X' ? (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">IVA No categorizado</Typography>
                    <Typography>{formatCurrency(factura.exento ?? 0)}</Typography>
                  </Stack>
                ) : (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">Exento</Typography>
                    <Typography>{formatCurrency(factura.exento ?? 0)}</Typography>
                  </Stack>
                )}
                {(factura.percepciones ?? 0) > 0 && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      component="span"
                      color="text.secondary"
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => setOpenImpuestosDialog(true)}
                    >
                      Otros impuestos
                      {(factura.impuestos?.length ?? 0) > 0 && (
                        <IconButton size="small" sx={{ ml: 0.5, py: 0 }} onClick={(e) => { e.stopPropagation(); setOpenImpuestosDialog(true); }}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Typography>
                    <Typography>{formatCurrency(factura.percepciones ?? 0)}</Typography>
                  </Stack>
                )}
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                  <Typography>Importe total</Typography>
                  <Typography>{formatCurrency(factura.importeTotal ?? 0)}</Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Dialog open={openImpuestosDialog} onClose={() => setOpenImpuestosDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Otros impuestos</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Cuenta</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Monto base</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Monto impuesto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(factura.impuestos ?? []).map((imp, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{imp.codigoImpuesto ?? '-'}</TableCell>
                    <TableCell align="right">{imp.ctaCtable ?? '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(imp.montoBaseImp ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(imp.montoImpuesto ?? 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImpuestosDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FacturaCompraDetalle;
